// URL parameters (§28) and deep links (§23.1 step 7). DOM-free except `readParams()`.
import { Difficulty, MapId, ModeId } from '@shared/ids';
import { normalizeRoomCode, type RoomDebug, type RoomSettings } from '@shared/protocol/messages';
import type { QualityTier } from '../render/Renderer';
import { stripBase } from './base';

/** `?debug=1` is 'stats'; the other values open a verification page. */
export type DebugPage = 'stats' | 'cosmetics' | 'vfx' | 'audio' | 'solids' | 'maps';

export interface Params {
  quick: boolean;
  /** `?room=CODE` (join; tests create it if missing). */
  room: string | null;
  /** `/r/CODE` path link. */
  pathRoom: string | null;
  autostart: boolean;
  /** 0–7 fixed bot count (botFill off). */
  bots: number | null;
  difficulty: Difficulty | null;
  seed: number | null;
  map: MapId | null;
  mode: ModeId | null;
  rounds: 3 | 5 | 8 | null;
  golden: boolean;
  roundSec: number | null;
  items: boolean;
  quality: QualityTier | null;
  nopost: boolean;
  mute: boolean;
  autoplay: boolean;
  name: string | null;
  skipIntro: boolean;
  /** Client-side artificial latency per direction, ms. */
  lag: number;
  debug: DebugPage | null;
  practice: boolean;
}

const MAP_NAMES: Readonly<Record<string, MapId>> = { teacup: MapId.TEACUP, pinball: MapId.PINBALL, cake: MapId.CAKE, spin: MapId.SPIN };
const MODE_NAMES: Readonly<Record<string, ModeId>> = { royale: ModeId.ROYALE, potato: ModeId.HOT_POTATO, crown: ModeId.CROWN_GRAB };
const DIFFICULTY_NAMES: Readonly<Record<string, Difficulty>> = { chill: Difficulty.CHILL, normal: Difficulty.NORMAL, spicy: Difficulty.SPICY };
const QUALITIES: readonly QualityTier[] = ['high', 'medium', 'low', 'potato'];
const DEBUG_PAGES: readonly DebugPage[] = ['cosmetics', 'vfx', 'audio', 'solids', 'maps'];
const PLAYLIST_FOR_MODE: Readonly<Record<number, RoomSettings['playlist']>> = {
  [ModeId.ROYALE]: 'royale', [ModeId.HOT_POTATO]: 'potato', [ModeId.CROWN_GRAB]: 'crown',
};
const ROOM_PATH = /^\/r\/([^/]+)\/?$/;

function lookup<T>(table: Readonly<Record<string, T>>, raw: string | null): T | null {
  if (raw === null) return null;
  const key = raw.toLowerCase();
  return Object.prototype.hasOwnProperty.call(table, key) ? table[key] : null;
}

function pathRoomCode(pathname: string): string | null {
  const m = ROOM_PATH.exec(pathname);
  if (!m) return null;
  try {
    return normalizeRoomCode(decodeURIComponent(m[1]));
  } catch {
    return null;
  }
}

function num(raw: string | null): number | null {
  if (raw === null || raw.trim() === '') return null;
  const v = Number(raw);
  return Number.isFinite(v) ? v : null;
}

/** Parses `location.search` and `location.pathname` into typed params; invalid values are ignored. */
export function parseParams(search: string, pathname = '/'): Params {
  const q = new URLSearchParams(search);
  const flag = (name: string): boolean => {
    const v = q.get(name);
    return v !== null && v !== '0' && v.toLowerCase() !== 'false';
  };

  const bots = num(q.get('bots'));
  const seed = num(q.get('seed'));
  const roundSec = num(q.get('roundSec'));
  const lag = num(q.get('lag'));
  const rounds = num(q.get('rounds'));
  const quality = q.get('quality');
  const debug = q.get('debug');
  const name = q.get('name');

  return {
    quick: flag('quick'),
    room: normalizeRoomCode(q.get('room')),
    pathRoom: pathRoomCode(pathname),
    autostart: flag('autostart'),
    bots: bots !== null && Number.isInteger(bots) && bots >= 0 && bots <= 7 ? bots : null,
    difficulty: lookup(DIFFICULTY_NAMES, q.get('difficulty')),
    seed: seed !== null ? seed >>> 0 : null,
    map: lookup(MAP_NAMES, q.get('map')),
    mode: lookup(MODE_NAMES, q.get('mode')),
    rounds: rounds === 3 || rounds === 5 || rounds === 8 ? rounds : null,
    golden: flag('golden'),
    roundSec: roundSec !== null && roundSec > 0 ? roundSec : null,
    items: q.get('items') === null || flag('items'),
    quality: QUALITIES.find((t) => t === quality) ?? null,
    nopost: flag('nopost'),
    mute: flag('mute'),
    autoplay: flag('autoplay'),
    name: name !== null && name.trim() !== '' ? name : null,
    skipIntro: flag('skipIntro'),
    lag: lag !== null && lag > 0 ? Math.round(lag) : 0,
    debug: debug === null ? null : debug === '1' ? 'stats' : (DEBUG_PAGES.find((p) => p === debug) ?? null),
    practice: flag('practice'),
  };
}

export function readParams(): Params {
  return parseParams(location.search, stripBase(location.pathname));
}

/**
 * Room settings and debug options for `createRoom` from params (`?autostart=1`, and `?room=CODE`
 * when the room does not exist yet). `?map`/`?mode` lock the map/playlist and, when both are
 * given, also force the first card.
 */
export function roomRequestFromParams(p: Params): { settings: Partial<RoomSettings>; debug: RoomDebug } {
  const settings: Partial<RoomSettings> = {};
  if (p.bots !== null) {
    settings.botFill = false;
    settings.botCount = p.bots;
  }
  if (p.difficulty !== null) settings.botDifficulty = p.difficulty;
  if (p.map !== null) settings.map = p.map;
  if (p.mode !== null) settings.playlist = PLAYLIST_FOR_MODE[p.mode];
  if (p.rounds !== null) settings.rounds = p.rounds;
  if (!p.items) settings.items = false;

  const debug: RoomDebug = {};
  if (p.room !== null) debug.code = p.room;
  if (p.seed !== null) debug.seed = p.seed;
  if (p.roundSec !== null) debug.roundSec = p.roundSec;
  if (p.map !== null && p.mode !== null) debug.firstCard = { mode: p.mode, map: p.map };
  if (p.golden) debug.goldenFirst = true;
  if (p.autostart) debug.startNow = true;
  return { settings, debug };
}
